import type React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";

import { useAuthStore } from "../state/authStore";
import { api } from "../utils/api";

// ── API shapes (mirror routers/admin.py) ──────────────────────────────
interface AdminTeam {
  id: string;
  name: string;
  owner: string | null;
  memberCount: number;
  enabledSeriesCount: number;
  createdAt: string | null;
  lastActivityAt: string | null;
}
interface AdminUser {
  id: string;
  displayName: string | null;
  email: string | null;
  iracingCustId: string;
  isActive: boolean;
  isPlatformAdmin: boolean;
  createdAt: string | null;
  lastLoginAt: string | null;
}
interface AdminAudit {
  id: string;
  actor: string | null;
  action: string;
  targetType: string | null;
  targetTeamId: string | null;
  targetUserId: string | null;
  ip: string | null;
  createdAt: string | null;
}
interface TeamDetail {
  id: string;
  name: string;
  enabledSeries: string[];
  createdAt: string | null;
  runCount: number;
  lastUploadAt: string | null;
  members: { userId: string; displayName: string | null; email: string | null; role: string; joinedAt: string | null }[];
}
type Tab = "teams" | "users" | "audit";

function fmtDate(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString();
}

export default function AdminConsole() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [tab, setTab] = useState<Tab>("teams");

  // Gate: non-admins never see this surface (server also 404s every endpoint).
  useEffect(() => {
    if (user && !user.isPlatformAdmin) navigate("/teams", { replace: true });
  }, [user, navigate]);

  return (
    <div className="page page--wide">
      <header style={styles.header}>
        <div>
          <div className="eyebrow">Platform</div>
          <h1 style={styles.title}>Admin console</h1>
          <p style={styles.subtitle}>Every team and user on StintSense. Read-only — all access is logged.</p>
        </div>
      </header>

      <nav style={styles.tabs}>
        {(["teams", "users", "audit"] as Tab[]).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            style={{ ...styles.tab, ...(tab === t ? styles.tabActive : {}) }}
            aria-current={tab === t ? "true" : undefined}
          >
            {t === "teams" ? "Teams" : t === "users" ? "Users" : "Audit log"}
          </button>
        ))}
      </nav>

      {tab === "teams" && <TeamsTab />}
      {tab === "users" && <UsersTab />}
      {tab === "audit" && <AuditTab />}
    </div>
  );
}

function usePaged<T>(path: string, q: string, pick: (r: any) => T[]) {
  const [rows, setRows] = useState<T[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const limit = 50;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ page: String(page), limit: String(limit) });
      if (q.trim()) params.set("q", q.trim());
      const data = await api<any>(`${path}?${params.toString()}`);
      setRows(pick(data));
      setTotal(data.total ?? 0);
    } catch {
      // Distinguish a failed load from a genuinely empty list — an admin can't act
      // on silent stale rows.
      setError("Couldn't load this list. Check your connection and try again.");
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, q, page]);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => { setPage(1); }, [q]);

  return { rows, total, page, setPage, loading, error, reload: load, limit };
}

/** Body of a data table — one place to render loading / error / empty / rows. */
function TableBody<T>({ cols, loading, error, rows, empty, onRetry, renderRow }: {
  cols: number;
  loading: boolean;
  error: string | null;
  rows: T[];
  empty: string;
  onRetry: () => void;
  renderRow: (row: T) => React.ReactNode;
}) {
  if (loading) return <tbody><tr><td colSpan={cols} style={styles.empty}>Loading…</td></tr></tbody>;
  if (error) {
    return (
      <tbody>
        <tr>
          <td colSpan={cols} style={styles.errorCell}>
            {error}{" "}
            <button type="button" className="btn btn-secondary" style={styles.retryBtn} onClick={onRetry}>Retry</button>
          </td>
        </tr>
      </tbody>
    );
  }
  if (!rows.length) return <tbody><tr><td colSpan={cols} style={styles.empty}>{empty}</td></tr></tbody>;
  return <tbody>{rows.map(renderRow)}</tbody>;
}

function Pager({ page, setPage, total, limit }: { page: number; setPage: (n: number) => void; total: number; limit: number }) {
  const pages = Math.max(1, Math.ceil(total / limit));
  return (
    <div style={styles.pager}>
      <span style={styles.pagerInfo}>{total} total · page {page} of {pages}</span>
      <div style={{ display: "flex", gap: "0.5rem" }}>
        <button type="button" className="btn" disabled={page <= 1} onClick={() => setPage(page - 1)}>Prev</button>
        <button type="button" className="btn" disabled={page >= pages} onClick={() => setPage(page + 1)}>Next</button>
      </div>
    </div>
  );
}

function TeamsTab() {
  const [q, setQ] = useState("");
  const { rows, total, page, setPage, loading, error, reload, limit } = usePaged<AdminTeam>("/api/admin/teams", q, (r) => r.teams || []);
  const [detail, setDetail] = useState<TeamDetail | null>(null);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [viewAsOpen, setViewAsOpen] = useState(false);
  const drawerRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLElement | null>(null);
  // Ref (not a dep) so opening the modal doesn't re-run the drawer focus effect —
  // its cleanup would steal focus back to the row link mid-open.
  const viewAsOpenRef = useRef(false);
  viewAsOpenRef.current = viewAsOpen;

  const openTeam = async (id: string) => {
    triggerRef.current = document.activeElement as HTMLElement; // restore focus here on close
    setDetailError(null);
    try {
      const data = await api<TeamDetail>(`/api/admin/teams/${id}`);
      setDetail(data);
    } catch {
      setDetailError("Couldn't load that team. Try again.");
    }
  };

  // Modal dialog behavior: focus into the drawer on open, Escape closes, Tab is
  // trapped inside it (aria-modal claims the background is gone, so focus must be
  // too), and focus returns to the opener on close.
  useEffect(() => {
    if (!detail) return;
    drawerRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (viewAsOpenRef.current) return; // the view-as modal owns the keyboard while open
      if (e.key === "Escape") { setDetail(null); return; }
      if (e.key !== "Tab" || !drawerRef.current) return;
      const f = drawerRef.current.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (!f.length) return;
      const first = f[0], last = f[f.length - 1], active = document.activeElement;
      if (!drawerRef.current.contains(active as Node)) { e.preventDefault(); first.focus(); }
      else if (e.shiftKey && active === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && active === last) { e.preventDefault(); first.focus(); }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      triggerRef.current?.focus();
    };
  }, [detail]);

  return (
    <section className="card" style={styles.card}>
      <input className="input" placeholder="Search teams by name…" value={q} onChange={(e) => setQ(e.target.value)} style={styles.search} />
      {detailError && <div style={styles.inlineError}>{detailError}</div>}
      <div style={styles.tableWrap}>
        <table style={styles.table}>
          <thead>
            <tr>
              <Th>Team</Th><Th>Owner</Th><Th>Members</Th><Th>Series</Th><Th>Created</Th><Th>Last activity</Th>
            </tr>
          </thead>
          <TableBody
            cols={6} loading={loading} error={error} rows={rows} empty="No teams found." onRetry={() => void reload()}
            renderRow={(t) => (
              <tr key={t.id} style={styles.rowStatic}>
                <Td>
                  <button type="button" style={styles.linkBtn} onClick={() => void openTeam(t.id)}>{t.name}</button>
                </Td>
                <Td>{t.owner || "—"}</Td>
                <Td>{t.memberCount}</Td>
                <Td>{t.enabledSeriesCount}</Td>
                <Td>{fmtDate(t.createdAt)}</Td>
                <Td>{fmtDate(t.lastActivityAt)}</Td>
              </tr>
            )}
          />
        </table>
      </div>
      <Pager page={page} setPage={setPage} total={total} limit={limit} />

      {detail && (
        <div style={styles.drawerScrim} onClick={() => setDetail(null)}>
          <div
            ref={drawerRef}
            role="dialog"
            aria-modal="true"
            aria-label={`Team ${detail.name}`}
            tabIndex={-1}
            style={styles.drawer}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={styles.drawerHead}>
              <div>
                <div className="eyebrow">Team</div>
                <h2 style={styles.drawerTitle}>{detail.name}</h2>
              </div>
              <button type="button" className="btn" onClick={() => setDetail(null)}>Close</button>
            </div>
            <div style={styles.metaGrid}>
              <Meta label="Runs" value={String(detail.runCount)} />
              <Meta label="Last upload" value={fmtDate(detail.lastUploadAt)} />
              <Meta label="Created" value={fmtDate(detail.createdAt)} />
              <Meta label="Series" value={String(detail.enabledSeries.length)} />
            </div>
            <div className="eyebrow" style={styles.sectionLabel}>Members</div>
            <div style={styles.memberList}>
              {detail.members.map((m) => (
                <div key={m.userId} style={styles.memberRow}>
                  <span>{m.displayName || "—"}</span>
                  <span style={styles.memberMeta}>{m.role} · {m.email || "—"}</span>
                </div>
              ))}
            </div>
            <div style={styles.viewAsBlock}>
              <button type="button" className="btn btn-secondary" onClick={() => setViewAsOpen(true)}>
                View as team (read-only)
              </button>
              <p style={styles.note}>Opens the team the way its members see it. Read-only, time-boxed, recorded in the audit log.</p>
            </div>
          </div>
        </div>
      )}

      {detail && viewAsOpen && (
        <ViewAsTeamModal teamId={detail.id} teamName={detail.name} onClose={() => setViewAsOpen(false)} />
      )}
    </section>
  );
}

// ── Phase 2: "view as team" read-only impersonation ───────────────────
function ViewAsTeamModal({ teamId, teamName, onClose }: { teamId: string; teamName: string; onClose: () => void }) {
  const [password, setPassword] = useState("");
  const [reason, setReason] = useState("");
  const [reasonTouched, setReasonTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const modalRef = useRef<HTMLDivElement | null>(null);

  const reasonShort = reason.trim().length < 10;
  const canSubmit = !busy && password.length > 0 && !reasonShort;

  // Same dialog idiom as SeriesAdminModals: focus in, Escape closes, Tab trapped,
  // body scroll locked, focus restored to the opener.
  useEffect(() => {
    const prevActive = document.activeElement as HTMLElement | null;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    modalRef.current?.querySelector<HTMLElement>("input, textarea, button")?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") { e.preventDefault(); onClose(); return; }
      if (e.key !== "Tab" || !modalRef.current) return;
      const list = Array.from(modalRef.current.querySelectorAll<HTMLElement>(
        'button:not([disabled]), input:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      ));
      if (!list.length) return;
      const first = list[0], last = list[list.length - 1], active = document.activeElement;
      if (!modalRef.current.contains(active as Node)) { e.preventDefault(); first.focus(); }
      else if (e.shiftKey && active === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && active === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
      prevActive?.focus?.();
    };
  }, [onClose]);

  const submit = async () => {
    setReasonTouched(true);
    if (reasonShort) {
      setError("Give a reason of at least 10 characters — it goes in the audit log.");
      return;
    }
    if (!password) {
      setError("Enter your password to confirm.");
      return;
    }
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const result = await api<{ status: string; teamId: string }>(`/api/admin/teams/${teamId}/impersonate`, {
        method: "POST",
        body: JSON.stringify({ reason: reason.trim(), password }),
      });
      // Hard navigate so the whole app re-fetches /me and the hazard banner mounts.
      window.location.assign(`/teams/${result.teamId || teamId}`);
    } catch (err: unknown) {
      const apiErr = err as { status?: number; message?: string };
      setBusy(false);
      if (apiErr.status === 401) setError("Password is incorrect.");
      else if (apiErr.status === 404) setError("That team no longer exists. Close this and refresh the list.");
      else if (apiErr.status === 422) setError("Give a reason of at least 10 characters — it goes in the audit log.");
      else if (apiErr.status === 503) setError("Support view is unavailable right now. Try again in a minute.");
      else setError(apiErr.message || "Couldn't start the support view. Try again.");
    }
  };

  return createPortal(
    <div style={styles.modalOverlay} onMouseDown={(e) => e.target === e.currentTarget && !busy && onClose()}>
      <div ref={modalRef} className="card" style={styles.modal} role="dialog" aria-modal="true" aria-label="View as team" tabIndex={-1}>
        <div style={styles.modalHead}>
          <span className="eyebrow">Support access</span>
          <h2 style={styles.modalTitle}>View as team</h2>
          <p style={styles.modalDek}>
            You'll see {teamName} the way its members do. Read-only, and the session ends on its own. This access is recorded.
          </p>
        </div>
        <label style={styles.field}>
          <span className="section-label" style={{ margin: 0 }}>Reason</span>
          <textarea
            className="input"
            rows={3}
            value={reason}
            maxLength={500}
            onChange={(e) => setReason(e.target.value)}
            onBlur={() => setReasonTouched(true)}
            placeholder="Ticket number or what you're checking"
            style={styles.reasonInput}
          />
          <span style={reasonTouched && reasonShort ? styles.fieldHintShort : styles.fieldHint}>
            At least 10 characters — it goes in the audit log.
          </span>
        </label>
        <label style={styles.field}>
          <span className="section-label" style={{ margin: 0 }}>Your password</span>
          <input
            className="input"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") void submit(); }}
          />
        </label>
        {error && <div style={styles.modalError}>{error}</div>}
        <div style={styles.modalActions}>
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="button" className="btn btn-primary" onClick={() => void submit()} disabled={!canSubmit}>
            {busy ? "Starting…" : "Start support view"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function UsersTab() {
  const [q, setQ] = useState("");
  const { rows, total, page, setPage, loading, error, reload, limit } = usePaged<AdminUser>("/api/admin/users", q, (r) => r.users || []);
  return (
    <section className="card" style={styles.card}>
      <input className="input" placeholder="Search users by name or email…" value={q} onChange={(e) => setQ(e.target.value)} style={styles.search} />
      <div style={styles.tableWrap}>
        <table style={styles.table}>
          <thead>
            <tr><Th>Name</Th><Th>Email</Th><Th>iRacing ID</Th><Th>Status</Th><Th>Joined</Th><Th>Last login</Th></tr>
          </thead>
          <TableBody
            cols={6} loading={loading} error={error} rows={rows} empty="No users found." onRetry={() => void reload()}
            renderRow={(u) => (
              <tr key={u.id} style={styles.rowStatic}>
                <Td>{u.displayName || "—"}{u.isPlatformAdmin && <span className="pill" style={styles.adminPill}>admin</span>}</Td>
                <Td>{u.email || "—"}</Td>
                <Td>{u.iracingCustId}</Td>
                <Td>{u.isActive ? "Active" : "Inactive"}</Td>
                <Td>{fmtDate(u.createdAt)}</Td>
                <Td>{fmtDate(u.lastLoginAt)}</Td>
              </tr>
            )}
          />
        </table>
      </div>
      <Pager page={page} setPage={setPage} total={total} limit={limit} />
    </section>
  );
}

function AuditTab() {
  const { rows, total, page, setPage, loading, error, reload, limit } = usePaged<AdminAudit>("/api/admin/audit", "", (r) => r.events || []);
  return (
    <section className="card" style={styles.card}>
      <div style={styles.tableWrap}>
        <table style={styles.table}>
          <thead>
            <tr><Th>When</Th><Th>Admin</Th><Th>Action</Th><Th>Target</Th><Th>IP</Th></tr>
          </thead>
          <TableBody
            cols={5} loading={loading} error={error} rows={rows} empty="No audit events yet." onRetry={() => void reload()}
            renderRow={(e) => (
              <tr key={e.id} style={styles.rowStatic}>
                <Td>{e.createdAt ? new Date(e.createdAt).toLocaleString() : "—"}</Td>
                <Td>{e.actor || "—"}</Td>
                <Td>{e.action}</Td>
                <Td>{e.targetType || "—"}</Td>
                <Td>{e.ip || "—"}</Td>
              </tr>
            )}
          />
        </table>
      </div>
      <Pager page={page} setPage={setPage} total={total} limit={limit} />
    </section>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return <th style={styles.th}>{children}</th>;
}
function Td({ children }: { children: React.ReactNode }) {
  return <td style={styles.td}>{children}</td>;
}
function Meta({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="eyebrow" style={styles.metaLabel}>{label}</div>
      <div style={styles.metaValue}>{value}</div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  header: { marginBottom: "1.25rem" },
  title: { margin: "0.35rem 0 0.2rem", fontSize: "1.6rem", fontWeight: 800, color: "var(--text-strong)" },
  subtitle: { margin: 0, color: "var(--text-secondary)", fontSize: "0.9rem" },
  tabs: { display: "flex", gap: "0.5rem", marginBottom: "1rem", borderBottom: "1px solid var(--rule)" },
  tab: { border: "none", background: "none", padding: "0.6rem 0.2rem", marginRight: "1rem", color: "var(--text-secondary)", fontSize: "0.95rem", fontWeight: 600, cursor: "pointer", borderBottom: "2px solid transparent" },
  tabActive: { color: "var(--text-strong)", borderBottomColor: "var(--live)" },
  card: { padding: "1rem", position: "relative" },
  search: { marginBottom: "0.85rem", maxWidth: "26rem" },
  inlineError: { marginBottom: "0.75rem", fontSize: "0.8125rem", color: "var(--flag-red)" },
  tableWrap: { overflowX: "auto" },
  table: { width: "100%", borderCollapse: "collapse", fontSize: "0.875rem" },
  // Sentence-case, ≥13px — the brand reserves uppercase for the .eyebrow kicker,
  // not data-table headers.
  th: { textAlign: "left", padding: "0.5rem 0.75rem", color: "var(--text-secondary)", fontWeight: 600, fontSize: "0.8125rem", borderBottom: "1px solid var(--rule)", whiteSpace: "nowrap" },
  td: { padding: "0.6rem 0.75rem", color: "var(--text-default)", borderBottom: "1px solid var(--rule-faint)", whiteSpace: "nowrap" },
  rowStatic: {},
  // Keyboard-accessible team opener (was a click-only <tr>).
  linkBtn: { border: "none", background: "none", padding: 0, color: "var(--live-strong)", fontWeight: 600, fontSize: "inherit", fontFamily: "inherit", cursor: "pointer", textAlign: "left" },
  empty: { padding: "1.5rem 0.75rem", textAlign: "center", color: "var(--text-secondary)" },
  errorCell: { padding: "1.25rem 0.75rem", textAlign: "center", color: "var(--flag-red)" },
  retryBtn: { marginLeft: "0.5rem" },
  adminPill: { marginLeft: "0.5rem" },
  pager: { display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: "0.85rem" },
  pagerInfo: { color: "var(--text-secondary)", fontSize: "0.8125rem" },
  drawerScrim: { position: "fixed", inset: 0, background: "rgba(2, 6, 14, 0.55)", zIndex: 80, display: "flex", justifyContent: "flex-end" },
  drawer: { width: "min(30rem, 100%)", height: "100%", overflowY: "auto", background: "var(--ink-1)", borderLeft: "1px solid var(--rule-strong)", padding: "1.25rem", outline: "none" },
  drawerHead: { display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "1rem", marginBottom: "1rem" },
  drawerTitle: { margin: "0.3rem 0 0", fontSize: "1.25rem", fontWeight: 800, color: "var(--text-strong)" },
  metaGrid: { display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "0.85rem", marginBottom: "1.25rem" },
  metaLabel: {},
  metaValue: { marginTop: "0.2rem", fontSize: "1.05rem", fontWeight: 700, color: "var(--text-strong)" },
  sectionLabel: { margin: "0.5rem 0 0.6rem" },
  memberList: { display: "grid", gap: "0.5rem" },
  memberRow: { display: "flex", justifyContent: "space-between", gap: "0.75rem", padding: "0.5rem 0.6rem", border: "1px solid var(--rule)", borderRadius: "var(--r-md)" },
  memberMeta: { color: "var(--text-secondary)", fontSize: "0.8125rem" },
  note: { margin: "0.6rem 0 0", fontSize: "0.8125rem", color: "var(--text-muted)", lineHeight: 1.5 },
  viewAsBlock: { marginTop: "1.5rem", paddingTop: "1rem", borderTop: "1px solid var(--rule)" },
  // View-as modal — SeriesAdminModals dialog idiom, above the drawer scrim (z 80).
  modalOverlay: { position: "fixed", inset: 0, zIndex: 1000, background: "rgba(6, 8, 12, 0.72)", display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "4.5rem 1rem", overflowY: "auto" },
  modal: { width: "100%", maxWidth: "460px", padding: "1.6rem 1.6rem 1.3rem", display: "flex", flexDirection: "column", gap: "1rem", outline: "none" },
  modalHead: { display: "flex", flexDirection: "column", gap: "0.3rem" },
  modalTitle: { fontFamily: "var(--font-display)", fontSize: "1.5rem", fontWeight: 700, margin: 0, color: "var(--text-strong)" },
  modalDek: { margin: 0, fontSize: "0.9rem", color: "var(--text-muted)", lineHeight: 1.45 },
  field: { display: "flex", flexDirection: "column", gap: "0.4rem" },
  reasonInput: { resize: "vertical", minHeight: "4.5rem", fontFamily: "var(--font-body)" },
  fieldHint: { fontSize: "0.8125rem", color: "var(--text-muted)", lineHeight: 1.4 },
  fieldHintShort: { fontSize: "0.8125rem", color: "var(--flag-red)", lineHeight: 1.4 },
  modalError: { fontSize: "0.875rem", color: "var(--flag-red)", lineHeight: 1.4 },
  modalActions: { display: "flex", justifyContent: "flex-end", gap: "0.6rem", marginTop: "0.4rem" },
};
